import { useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { resetPassword } from "../../../services/authService";
import { resetPasswordSchema, zodErrorToFirstMessage } from "../../../schemas/formSchemas";
import FormField from "../../../components/ui/FormField";
import PasswordField from "../../../components/ui/PasswordField";
import Button from "../../../components/ui/Button";
import { ErrorCard } from "../../../components/ui/FeedbackStates";
import "./ResetPassword.css";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const token = useMemo(() => String(searchParams.get("token") || "").trim(), [searchParams]);
  const userId = useMemo(() => Number(searchParams.get("user_id")), [searchParams]);

  const [form, setForm] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const parsed = resetPasswordSchema.safeParse({ ...form, user_id: userId, token });
    if (!parsed.success) {
      setError(zodErrorToFirstMessage(parsed.error));
      return;
    }

    setLoading(true);
    try {
      await resetPassword({
        user_id: parsed.data.user_id,
        token: parsed.data.token,
        new_password: parsed.data.password,
      });
      setDone(true);
      setTimeout(() => navigate("/"), 800);
    } catch (err) {
      console.error(err);
      const apiMessage = err?.data?.message || err?.data?.error || err?.message;
      setError(apiMessage || "Unable to reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card reset-card">
        <div className="login-brand">
          <div className="brand-mark">
            <div className="brand-text">My AsmitA</div>
          </div>
        </div>

        <div className="login-panel">
          <div className="login-header">
            <h1>Set Password</h1>
            <p>Choose a new password to activate your account.</p>
          </div>

          {error ? (
            <div style={{ marginBottom: "16px" }}>
              <ErrorCard title="Password Reset Failed" message={error} />
            </div>
          ) : null}
          {done ? <div className="reset-success">Password updated. Redirecting…</div> : null}

          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
            <FormField label="New Password" required>
              <PasswordField
                id="new-password"
                placeholder="Enter new password"
                value={form.password}
                onChange={(e) => setForm((prev) => ({ ...prev, password: e.target.value }))}
                showStrengthIndicator
                disabled={loading || done}
                required
              />
            </FormField>

            <FormField label="Confirm Password" required>
              <PasswordField
                id="confirm-password"
                placeholder="Confirm your password"
                value={form.confirm}
                onChange={(e) => setForm((prev) => ({ ...prev, confirm: e.target.value }))}
                disabled={loading || done}
                required
              />
            </FormField>

            <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginTop: "8px" }}>
              <Button type="submit" variant="primary" loading={loading} disabled={loading || done} style={{ width: "100%" }}>
                SET PASSWORD
              </Button>

              <Button
                variant="outline"
                type="button"
                onClick={() => navigate("/")}
                disabled={loading}
                style={{ width: "100%" }}
              >
                Back to login
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
